import type { TranslationTag } from '@ps/web-core-intl';

import { CardType } from '../../../../../../../types/cardType';

import {
  TXTWCL_A_photo_that_shows_only_the_fi_ELL_1,
  TXTWCL_A_screenshot_of_the_account_us_ELL,
  TXTWCL_A_screenshot_of_your_XAccountX_ELL,
  TXTWCL_A_screenshot_of_your_XMy_Accou_ELL_1,
  TXTWCL_A_screenshot_of_your_XPersonal_ELL,
  TXTWCL_A_screenshot_of_your_XPersonal_ELL_1,
  TXTWCL_A_screenshot_of_your_XXBrandXX_ELL,
  TXTWCL_An_official_document_from_the__ELL,
  TXTWCL_Bank_identification_or_logo,
  TXTWCL_Blurry_or_distorted_images,
  TXTWCL_Clear_photo_of_bank_statement_,
  TXTWCL_For_virtual_card_paymentsX_we__ELL,
  TXTWCL_N0_as_a_payment_method,
  TXTWCL_Outdated_or_incorrect_informat_ELL,
  TXTWCL_Photocopies,
  TXTWCL_Photos_that_show_your_full_car_ELL,
  TXTWCL_Scanned_coloured_copy_of_bank__ELL,
  TXTWCL_Screenshot_of_online_banking_X_ELL,
  TXTWCL_Screenshot_of_online_banking_p_ELL,
  TXTWCL_Screenshot_of_the_XSettingsX_p_ELL,
  TXTWCL_Screenshot_of_your_Account_Ove_ELL,
  TXTWCL_Screenshot_of_your_N0_Wallet_s_ELL,
  TXTWCL_Screenshot_of_your_N0_XMy_Acco_ELL,
  TXTWCL_Screenshot_of_your_N0_XPersona_ELL,
  TXTWCL_Screenshot_of_your_N0_account__ELL,
  TXTWCL_Screenshot_of_your_XPersonal_D_ELL,
  TXTWCL_Screenshot_of_your_virtual_car_ELL,
  TXTWCL_Screenshot_or_photo_of_the_Mas_ELL_1,
  TXTWCL_Screenshot_your_N0_XProfileX_o_ELL,
  TXTWCL_Screenshots_with_personal_deta_ELL,
  TXTWCL_Shows_your_nameX_IBAN_and_Swif_ELL,
  TXTWCL_The_document_must_be_less_than_ELL,
  TXTWCL_URL_must_be_visible,
  TXTWCL_Upload_a_document_that_contain_ELL,
  TXTWCL_Upload_a_screenshot_of_a_bank__ELL,
  TXTWCL_Upload_a_screenshot_or_photo_t_ELL,
  TXTWCL_Upload_a_screenshot_that_conta_ELL,
  TXTWCL_Upload_a_screenshot_to_verify__ELL,
  TXTWCL_Verifying_your_payment_method__ELL_1,
  TXTWCL_We_needX,
  TXTWCL_We_need_both_of_the_followingX,
  TXTWCL_We_need_one_of_the_followingX,
  TXTWCL_We_will_not_be_able_to_acceptX,
  TXTWCL_Your_full_address,
  TXTWCL_Your_information_must_be_the_s_ELL,
  TXTWCL_Your_nameX_addressX_email_addr_ELL
} from './translationUtils.messages.json';

interface DocumentsType {
  title: TranslationTag;
  options: TranslationTag[];
}

const subtitleDefault = TXTWCL_Upload_a_screenshot_that_conta_ELL;

const defaultRequiredDocuments = {
  title: TXTWCL_We_will_not_be_able_to_acceptX,
  options: [TXTWCL_Blurry_or_distorted_images, TXTWCL_Outdated_or_incorrect_informat_ELL]
};

export const defaultMessages = {
  subtitle: subtitleDefault,
  acceptedDocuments: {
    title: TXTWCL_We_needX,
    options: [TXTWCL_A_screenshot_of_the_account_us_ELL]
  },
  requiredDocuments: defaultRequiredDocuments
};

export const translationMap: Partial<
  Record<
    CardType,
    { subtitle: TranslationTag; acceptedDocuments: DocumentsType; requiredDocuments: DocumentsType }
  >
> = {
  [CardType.ApplePay]: {
    subtitle: TXTWCL_Upload_a_screenshot_to_verify__ELL,
    acceptedDocuments: {
      title: TXTWCL_We_need_one_of_the_followingX,
      options: [
        TXTWCL_Screenshot_of_your_N0_Wallet_s_ELL,
        TXTWCL_Screenshot_of_your_virtual_car_ELL
      ]
    },
    requiredDocuments: defaultRequiredDocuments
  },
  [CardType.DBT]: {
    subtitle: TXTWCL_Upload_a_screenshot_of_a_bank__ELL,
    acceptedDocuments: {
      title: TXTWCL_We_needX,
      options: [
        TXTWCL_The_document_must_be_less_than_ELL,
        TXTWCL_Shows_your_nameX_IBAN_and_Swif_ELL,
        TXTWCL_Your_full_address,
        TXTWCL_Your_information_must_be_the_s_ELL,
        TXTWCL_Bank_identification_or_logo
      ]
    },
    requiredDocuments: defaultRequiredDocuments
  },
  [CardType.WireCashout]: {
    subtitle: TXTWCL_Upload_a_screenshot_of_a_bank__ELL,
    acceptedDocuments: {
      title: TXTWCL_We_needX,
      options: [
        TXTWCL_The_document_must_be_less_than_ELL,
        TXTWCL_Shows_your_nameX_IBAN_and_Swif_ELL,
        TXTWCL_Your_full_address,
        TXTWCL_Your_information_must_be_the_s_ELL,
        TXTWCL_Bank_identification_or_logo
      ]
    },
    requiredDocuments: defaultRequiredDocuments
  },
  [CardType.ECheck]: {
    subtitle: TXTWCL_Upload_a_screenshot_of_a_bank__ELL,
    acceptedDocuments: {
      title: TXTWCL_We_need_one_of_the_followingX,
      options: [
        TXTWCL_Screenshot_of_online_banking_p_ELL,
        TXTWCL_Scanned_coloured_copy_of_bank__ELL,
        TXTWCL_Clear_photo_of_bank_statement_
      ]
    },
    requiredDocuments: defaultRequiredDocuments
  },
  [CardType.InstantBank]: {
    subtitle: TXTWCL_Upload_a_screenshot_of_a_bank__ELL,
    acceptedDocuments: {
      title: TXTWCL_We_need_one_of_the_followingX,
      options: [
        TXTWCL_Screenshot_of_online_banking_p_ELL,
        TXTWCL_Scanned_coloured_copy_of_bank__ELL,
        TXTWCL_Clear_photo_of_bank_statement_
      ]
    },
    requiredDocuments: defaultRequiredDocuments
  },
  [CardType.EcoPayZ]: {
    subtitle: subtitleDefault,
    acceptedDocuments: {
      title: TXTWCL_We_needX,
      options: [
        TXTWCL_A_screenshot_of_your_XAccountX_ELL,
        TXTWCL_A_screenshot_of_your_XMy_Accou_ELL_1,
        TXTWCL_URL_must_be_visible
      ]
    },
    requiredDocuments: defaultRequiredDocuments
  },
  [CardType.InstaDebit]: {
    subtitle: subtitleDefault,
    acceptedDocuments: {
      title: TXTWCL_We_needX,
      options: [
        TXTWCL_Screenshot_your_N0_XProfileX_o_ELL,
        TXTWCL_Your_nameX_addressX_email_addr_ELL
      ]
    },
    requiredDocuments: defaultRequiredDocuments
  },
  [CardType.LuxonPay]: {
    subtitle: subtitleDefault,
    acceptedDocuments: {
      title: TXTWCL_We_needX,
      options: [TXTWCL_A_screenshot_of_your_XPersonal_ELL, TXTWCL_URL_must_be_visible]
    },
    requiredDocuments: defaultRequiredDocuments
  },
  [CardType.MBWay]: {
    subtitle: subtitleDefault,
    acceptedDocuments: {
      title: TXTWCL_We_needX,
      options: [TXTWCL_Screenshot_of_your_N0_account__ELL]
    },
    requiredDocuments: defaultRequiredDocuments
  },
  [CardType.Neteller2]: {
    subtitle: subtitleDefault,
    acceptedDocuments: {
      title: TXTWCL_We_needX,
      options: [TXTWCL_Screenshot_of_your_Account_Ove_ELL, TXTWCL_URL_must_be_visible]
    },
    requiredDocuments: defaultRequiredDocuments
  },
  [CardType.PayPal]: {
    subtitle: subtitleDefault,
    acceptedDocuments: {
      title: TXTWCL_We_needX,
      options: [TXTWCL_A_screenshot_of_your_XAccountX_ELL, TXTWCL_URL_must_be_visible]
    },
    requiredDocuments: defaultRequiredDocuments
  },
  [CardType.PaySafe]: {
    subtitle: subtitleDefault,
    acceptedDocuments: {
      title: TXTWCL_We_needX,
      options: [TXTWCL_Screenshot_of_the_XSettingsX_p_ELL, TXTWCL_URL_must_be_visible]
    },
    requiredDocuments: defaultRequiredDocuments
  },
  [CardType.MBookOBT]: {
    subtitle: subtitleDefault,
    acceptedDocuments: {
      title: TXTWCL_We_needX,
      options: [TXTWCL_A_screenshot_of_your_XPersonal_ELL_1, TXTWCL_URL_must_be_visible]
    },
    requiredDocuments: defaultRequiredDocuments
  },
  [CardType.Skrill1Tap]: {
    subtitle: subtitleDefault,
    acceptedDocuments: {
      title: TXTWCL_We_needX,
      options: [TXTWCL_Screenshot_of_your_N0_XPersona_ELL, TXTWCL_URL_must_be_visible]
    },
    requiredDocuments: defaultRequiredDocuments
  },
  [CardType.MBookers]: {
    subtitle: subtitleDefault,
    acceptedDocuments: {
      title: TXTWCL_We_needX,
      options: [TXTWCL_Screenshot_of_your_N0_XPersona_ELL, TXTWCL_URL_must_be_visible]
    },
    requiredDocuments: defaultRequiredDocuments
  },
  [CardType.WebMoney]: {
    subtitle: subtitleDefault,
    acceptedDocuments: {
      title: TXTWCL_We_needX,
      options: [TXTWCL_Screenshot_of_your_N0_XMy_Acco_ELL, TXTWCL_URL_must_be_visible]
    },
    requiredDocuments: defaultRequiredDocuments
  },
  [CardType.Interac]: {
    subtitle: TXTWCL_Upload_a_screenshot_that_conta_ELL,
    acceptedDocuments: {
      title: TXTWCL_We_needX,
      options: [
        TXTWCL_Screenshot_of_online_banking_X_ELL,
        TXTWCL_N0_as_a_payment_method,
        TXTWCL_URL_must_be_visible
      ]
    },
    requiredDocuments: {
      title: TXTWCL_We_will_not_be_able_to_acceptX,
      options: [
        TXTWCL_Blurry_or_distorted_images,
        TXTWCL_Outdated_or_incorrect_informat_ELL,
        TXTWCL_Photocopies
      ]
    }
  },
  [CardType.MuchBetter]: {
    subtitle: TXTWCL_Upload_a_document_that_contain_ELL,
    acceptedDocuments: {
      title: TXTWCL_We_need_both_of_the_followingX,
      options: [
        TXTWCL_Screenshot_of_your_XPersonal_D_ELL,
        TXTWCL_Screenshot_or_photo_of_the_Mas_ELL_1
      ]
    },
    requiredDocuments: {
      title: TXTWCL_We_will_not_be_able_to_acceptX,
      options: [
        TXTWCL_Blurry_or_distorted_images,
        TXTWCL_Outdated_or_incorrect_informat_ELL,
        TXTWCL_Photos_that_show_your_full_car_ELL
      ]
    }
  },
  [CardType.Visa]: {
    subtitle: TXTWCL_Verifying_your_payment_method__ELL_1,
    acceptedDocuments: {
      title: TXTWCL_We_needX,
      options: [TXTWCL_A_photo_that_shows_only_the_fi_ELL_1]
    },
    requiredDocuments: {
      title: TXTWCL_We_will_not_be_able_to_acceptX,
      options: [
        TXTWCL_Blurry_or_distorted_images,
        TXTWCL_Outdated_or_incorrect_informat_ELL,
        TXTWCL_Photos_that_show_your_full_car_ELL
      ]
    }
  },
  [CardType.MasterCard]: {
    subtitle: TXTWCL_Verifying_your_payment_method__ELL_1,
    acceptedDocuments: {
      title: TXTWCL_We_needX,
      options: [TXTWCL_A_photo_that_shows_only_the_fi_ELL_1]
    },
    requiredDocuments: {
      title: TXTWCL_We_will_not_be_able_to_acceptX,
      options: [
        TXTWCL_Blurry_or_distorted_images,
        TXTWCL_Outdated_or_incorrect_informat_ELL,
        TXTWCL_Photos_that_show_your_full_car_ELL
      ]
    }
  },
  [CardType.Solo]: {
    subtitle: TXTWCL_Verifying_your_payment_method__ELL_1,
    acceptedDocuments: {
      title: TXTWCL_We_needX,
      options: [TXTWCL_A_photo_that_shows_only_the_fi_ELL_1]
    },
    requiredDocuments: {
      title: TXTWCL_We_will_not_be_able_to_acceptX,
      options: [
        TXTWCL_Blurry_or_distorted_images,
        TXTWCL_Outdated_or_incorrect_informat_ELL,
        TXTWCL_Photos_that_show_your_full_car_ELL
      ]
    }
  },
  [CardType.Multibanco]: {
    subtitle: TXTWCL_Upload_a_screenshot_or_photo_t_ELL,
    acceptedDocuments: {
      title: TXTWCL_We_need_one_of_the_followingX,
      options: [
        TXTWCL_A_screenshot_of_your_XXBrandXX_ELL,
        TXTWCL_An_official_document_from_the__ELL,
        TXTWCL_For_virtual_card_paymentsX_we__ELL
      ]
    },
    requiredDocuments: {
      title: TXTWCL_We_will_not_be_able_to_acceptX,
      options: [
        TXTWCL_Blurry_or_distorted_images,
        TXTWCL_Outdated_or_incorrect_informat_ELL,
        TXTWCL_Screenshots_with_personal_deta_ELL
      ]
    }
  }
};
